import { Cache, getHash } from './cache';
import { IDbCache } from '../models/cache';
import { ICacheClearArgs } from '../models/cache-clear-args';
import { ICacheReadArgs } from '../models/cache-read-args';
import { ICachedValue } from '../models/cached-value';
import { SharedCacheBuckets } from '../models/shared-cache-buckets';
import { SharedCacheIds } from '../models/shared-cache-ids';
import { ISillyLogger } from '../models/silly-logger';

/*
 This "cacher" maintains objects in memory for a period of time. Serverless wipes out modules after handling a
 request, so storing a local dictionary wouldn't help us -- that's why we store on `global`.
 */
const memoryCacheKey = 'memory:cache';
/* istanbul ignore else */
if (!global[memoryCacheKey]) {
  global[memoryCacheKey] = {};
}
let memoryCache: { [bucket: string]: ICachedValue } = global[memoryCacheKey];

const oneMinuteInMilliseconds = 60000;

export class MemoryCache extends Cache {
  protected cacheName = 'Memory';
  private pollingId: NodeJS.Timeout;
  private lastClear = {
    [SharedCacheIds.All]: Date.now(),
  };

  constructor(
    private args?: {
      dbCache?: IDbCache;
      logger?: ISillyLogger;
    },
  ) {
    super(args?.logger);
  }

  public async read<T>(args: ICacheReadArgs<T>): Promise<T> {
    if (process.env.DISABLE_MEMORY_CACHE === 'true') {
      return args.retrieve();
    }
    const hash = getHash(args.bucket, args.id);
    const cached = memoryCache[hash];
    return this.handleCachedRead(cached, args);
  }

  public async clear(args?: ICacheClearArgs) {
    if (!args) {
      this.lastClear[SharedCacheIds.All] = Date.now();
      memoryCache = global[memoryCacheKey] = {};
    } else {
      const hash = getHash(args.bucket, args.id);
      memoryCache[hash] = null;
    }
  }

  public async clearByBucket(bucketToClear: string) {
    this.lastClear[bucketToClear] = Date.now();
    this.getHashesByBucket(bucketToClear).forEach(
      bucket => (memoryCache[bucket] = null),
    );
  }

  protected saveToCache(cached: ICachedValue) {
    memoryCache[cached.hash] = cached;
  }

  public startPollingDbForCacheClearing() {
    if (!this.args?.dbCache) {
      throw new Error(
        'Cannot poll for cache clearing without having the db cache defined. Please see in-a-cache README for more details.',
      );
    }
    this.pollingId = setInterval(async () => {
      const clearMap = await this.args?.dbCache.readByBucket(
        SharedCacheBuckets.LastMemoryCacheClear,
      );
      await Promise.all(
        Object.keys(clearMap)
          .filter(bucket => clearMap[bucket] > (this.lastClear[bucket] || 0))
          .map(bucket =>
            bucket === SharedCacheIds.All
              ? this.clear()
              : this.clearByBucket(bucket),
          ),
      );
    }, oneMinuteInMilliseconds);
  }

  public stopPollingDbForCacheClearing() {
    clearInterval(this.pollingId);
    this.pollingId = null;
  }

  private getHashesByBucket<T>(bucket: string): string[] {
    return Object.keys(memoryCache).filter(hash =>
      hash.startsWith(bucket + ':'),
    );
  }
}
